import type { EventBus, EventHandler, EventName } from './types';
import { logger } from '../logger/Logger';

class EventBusImpl implements EventBus {
  private map = new Map<EventName, Set<(payload: never) => void>>();

  on<K extends EventName>(event: K, handler: EventHandler<K>): () => void {
    let set = this.map.get(event);
    if (!set) {
      set = new Set();
      this.map.set(event, set);
    }
    set.add(handler as (payload: never) => void);
    return () => {
      this.off(event, handler);
    };
  }

  off<K extends EventName>(event: K, handler: EventHandler<K>): void {
    const set = this.map.get(event);
    if (!set) return;
    set.delete(handler as (payload: never) => void);
    if (set.size === 0) this.map.delete(event);
  }

  emit<K extends EventName>(event: K, payload: EventMapResolve<K>): void {
    const set = this.map.get(event);
    if (!set) return;
    // Snapshot para permitir unsubscribe durante emit sem bagunçar iteração.
    const handlers = Array.from(set) as Array<(p: EventMapResolve<K>) => void>;
    for (const h of handlers) {
      try {
        h(payload);
      } catch (err) {
        logger.error('event-bus.handler-failed', err as Error, { event });
      }
    }
  }

  clear(): void {
    this.map.clear();
  }
}

// Helper type to keep emit signature working with declaration merging.
type EventMapResolve<K extends EventName> = import('./types').EventMap[K];

let _instance: EventBus | null = null;

export function getEventBus(): EventBus {
  if (!_instance) _instance = new EventBusImpl();
  return _instance;
}

export function setEventBusForTests(impl: EventBus): void {
  _instance = impl;
}

export function resetEventBusForTests(): void {
  _instance = null;
}

export const eventBus: EventBus = new Proxy({} as EventBus, {
  get(_t, prop: keyof EventBus) {
    const inst = getEventBus();
    const fn = inst[prop];
    return typeof fn === 'function' ? fn.bind(inst) : fn;
  },
});
